import { Injectable } from '@angular/core';
import { Logger } from '@utils/logger';
import { parseNumber } from '@utils/text/number-utils';
import { SeriesOptionsType } from 'highcharts';
import { IndexesService } from '../../services/indexes.service';
import { Indexes, IndexLevels } from '../../types/model/indexes';
import { IndexesPerformanceBRConfig } from '../../types/us-index.type';
import USIndexHistoricalPerformanceQuery from '../../graphql/us-index-historical-performance.graphql';
import USIndexHistoricalCustomPerformanceQuery from '../../graphql/us-index-historical-custom-performance.graphql';
import {
  CustomSeriesOptions,
  UsIndexPerformanceBaseService,
} from './us-index-performance-base.service';

import moment from 'moment';
import { USIndexDateRangeInitialValuesForHistorical } from '../us-index.config';
import { HighchartsThemeService, ResponsiveService } from '@frk/eds-components';

const logger = Logger.getLogger('UsIndexPerformanceHistoricalService');

@Injectable({
  providedIn: 'root',
})
export class UsIndexPerformanceHistoricalService extends UsIndexPerformanceBaseService {
  constructor(
    private indexesService: IndexesService,
    protected highchartsTheme: HighchartsThemeService,
    protected responsiveService: ResponsiveService
  ) {
    super(highchartsTheme, responsiveService);

    this.responsiveService.isHandheld$().subscribe(() => {
      this.state.config.chartConfig.highChartOptions = this.fetchChartOptions();
      this.usIndexPerformanceState$.next(this.state);
    });
  }

  /**
   * Fetches indexes data and assign state.
   * @param brConfig bloomreach configuration for us-index
   * @param brLabels labels from resource bundle
   */
  populate(brConfig: IndexesPerformanceBRConfig, brLabels: object): void {
    // Save brConfig for custom performance API call.
    this.brConfig = brConfig;

    // Save brLabels for fetching relevant labels.
    this.brLabels = brLabels;

    this.indexesService
      .register(USIndexHistoricalPerformanceQuery, {
        fundId: brConfig?.fundId,
        shareClassCode: brConfig?.shareClassCode,
      })
      .subscribe(
        async (indexesData) => {
          logger.debug(`Mapped Indexes Data: ${indexesData}`);

          this.mapState(indexesData);
        },
        (error) => {
          logger.error(`Error in fetching Indexes Data: ${error}`);

          this.state.hasLoadedFullFeed = false;
          this.usIndexPerformanceState$.next(this.state);
        }
      );
  }

  /**
   * Assign state and process chart.
   * @param indexesData mapped indexes data from feed
   */
  private async mapState(indexesData: Indexes): Promise<void> {
    this.state.data.daily = indexesData.indexPerformance;

    const historicalData: IndexLevels[] = indexesData.indexLevels;

    this.state.data.indexLevels = historicalData;
    this.state.config.incDateStd =
      historicalData[historicalData.length - 1]?.asOfDateStd;
    this.state.config.asOfDateStd = historicalData[0]?.asOfDateStd;
    this.state.config.asOfDate = historicalData[0]?.asOfDate;

    this.state.config.chartConfig.dateRangeEnum = USIndexDateRangeInitialValuesForHistorical;
    this.state.config.chartConfig.highChartOptions = this.fetchChartOptions();

    // Assign chart lables & caveats.
    this.state.config.chartConfig.chartTitle = this.brLabels[
      'us-index-tab-historical'
    ];
    this.state.config.chartConfig.proximalPlacementTop =
      'USIndexHistoricalChartProximalTop';
    this.state.config.chartConfig.proximalPlacementBottom =
      'USIndexHistoricalChartProximalBottom';
    this.state.config.chartConfig.caveatPlacement =
      'USIndexHistoricalChartCaveat';
    this.state.config.chartConfig.tooltip = this.brLabels[
      'us-index-historical-chart-tooltip'
    ];
    this.state.config.chartConfig.seriesName = this.brConfig.fundName;

    // Create historical index chart using default date range.
    await this.createHistoricalChart(
      this.state.config.chartConfig.selectedToggleRange
    );

    this.state.hasLoadedFullFeed = true;
    this.usIndexPerformanceState$.next(this.state);
  }

  /**
   * Filter the historical indexes chart data as per the toggle range selected.
   * @param selectedToggleRange selected range toggle value
   * @param fromDate from date value selected from date picker, if available
   * @param toDate to date value selected from date picker, if available
   */
  async createHistoricalChart(
    selectedToggleRange: string,
    fromDate?: string,
    toDate?: string
  ): Promise<void> {
    const {
      filteredHistoricalData: filteredIndexLevelData,
      fromDateStd,
      toDateStd,
    } = this.getFilteredDataByDateRange(
      this.state.data.indexLevels,
      selectedToggleRange,
      fromDate,
      toDate
    );

    // Assign annualized return as per date range selected.
    if (selectedToggleRange === 'CUSTOM_RANGE') {
      this.state.data.custom = (
        await this.fetchIndexCustomPerformance(fromDate, toDate)
      ).indexCustomPerformance;
    }

    this.state.config.chartConfig.annualizedReturn = this.getAnnualizedReturnByTimePeriod(
      selectedToggleRange,
      this.state.data.daily,
      this.state.data.custom
    );

    this.state.config.chartConfig.highChartOptions.series = this.fetchChartSeries(
      filteredIndexLevelData
    );

    // Also, set the date values in state.
    this.state.config.chartConfig.selectedToggleRange = selectedToggleRange;
    this.state.config.chartConfig.customRange.fromDateStd = fromDateStd;
    this.state.config.chartConfig.customRange.toDateStd = toDateStd;

    // Below condition will not trigger state update on initial load.
    // Let it be done in above 'mapState' method.
    // Next time, when range selection happens, then trigger state change.
    if (this.state.hasLoadedFullFeed) {
      this.usIndexPerformanceState$.next(this.state);
    }
  }

  /**
   * Fetch custom indexes data and returns its promise.
   * @param fromDate from date value selected from date picker, if available
   * @param toDate to date value selected from date picker, if available
   */
  private fetchIndexCustomPerformance(
    fromDate: string,
    toDate: string
  ): Promise<Indexes> {
    return this.indexesService
      .register(USIndexHistoricalCustomPerformanceQuery, {
        fundId: this.brConfig?.fundId,
        shareClassCode: this.brConfig?.shareClassCode,
        startdate: Number(moment(fromDate).format('YYYYMMDD')),
        enddate: Number(moment(toDate).format('YYYYMMDD')),
      })
      .toPromise();
  }

  /**
   * Fetch chart series data as per index levels.
   * @param indexLevelHistory index level history available
   */
  private fetchChartSeries(
    indexLevelHistory: IndexLevels[]
  ): SeriesOptionsType[] {
    const indexLevelValues = this.convertIndexLevelHistoryIntoSeries(
      indexLevelHistory
    );
    const currencySymbol = this.getCurrencySymbol(
      this.state.config.currencyCode
    );

    // COREWEB-6246 remove HTML from label
    // we cannot remove whole html, because sometimes label contains html tag
    // we need to remove only tooltip code, which is added automatically in getLabel
    // code is removing <a> tag with its content from label
    // first we need to remove new lines, which are quite often in labels
    // this can be refactored in the future and support for HTML in charts title may be easily added - extensive testing required
    const label = this.state.config.chartConfig.seriesName
      ?.replace(/(\r\n|\n|\r)/gm, '')
      .replace(/<a([^>]+?)([^>]*?)>(.*?)<\/a>/gi, '');

    const indexLevelSeries: CustomSeriesOptions = {
      name: label,
      type: 'line',
      min: 0,
      tooltip: {
        pointFormatter: this.pointFormatterProvider(),
      },
      data: indexLevelValues,
      currencySymbol,
    };

    return [indexLevelSeries];
  }

  /**
   * Convert index level histories into chart series.
   * @param indexLevelHistory index level historic values
   */
  private convertIndexLevelHistoryIntoSeries(
    indexLevelHistory: IndexLevels[]
  ): [number, number][] {
    const indexLevelValues = [];

    if (indexLevelHistory) {
      const navHistoryLength: number = indexLevelHistory.length - 1;
      for (let c = navHistoryLength; c >= 0; c--) {
        const navPoint: IndexLevels = indexLevelHistory[c];
        let ts: number = this.formatStdDateToUnixTimeStamp(
          navPoint.asOfDateStd
        );
        const val: number = parseNumber(navPoint.indexLevel);

        // COREWEB-1539 : edge case 2017-05-05 00:00 being parsed as 2017-05-04 24:00, add a second
        // ps quicker than .set('second', 1)
        ts += 1 * 1000;

        if (val && !isNaN(val)) {
          indexLevelValues.push([ts, val]);
        }
      }
    }

    return indexLevelValues;
  }
}
